/**
 * Sortable vertical lists — roadmap 5.5.
 *
 * The curriculum tree is two nested sortable lists: modules within a course,
 * lessons within a module. Both are here rather than in the instructor portal
 * because Phase 9 reorders ticket types the same way, and a second hand-rolled
 * drag implementation is a second chance to drop the keyboard path.
 *
 * Three decisions worth not rediscovering:
 *
 * **Activation is handle-only.** `useSortable`'s listeners go on the grip
 * button, never the row. Nesting one `DndContext` inside another otherwise has
 * both of them claiming the same pointer press, and a lesson drag starts
 * dragging its module. It also leaves the rest of the row clickable, which is
 * what makes "click a lesson to edit it" coexist with dragging at all.
 *
 * **Movement is locked to the vertical axis** by zeroing the transform's `x`.
 * `@dnd-kit/modifiers` would do it too, but this is one line and — more to the
 * point — it is direction-agnostic: there is no left or right to get backwards
 * in Arabic, only along-the-list and across-it.
 *
 * **Dragging is never the only way to reorder.** A pointer drag is unusable
 * with a keyboard, unreliable with assistive tech, and awkward on a phone.
 * `KeyboardSensor` covers the first, and every caller is expected to offer
 * move-up/move-down actions as well — see `CurriculumScreen`.
 */
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type Announcements,
  type DragEndEvent,
} from '@dnd-kit/core'
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { GripVertical } from 'lucide-react'

import { cn } from './cn'

/**
 * Announcement builders. Callbacks rather than strings because the position and
 * total are only known at announce time, and design-system rule 1 keeps the
 * words themselves outside the component — the caller closes over `t`.
 */
export type SortableAnnouncements = {
  /** Read when a grip handle takes focus. */
  instructions: string
  lifted: (args: { item: string; position: number; total: number }) => string
  moved: (args: { item: string; position: number; total: number }) => string
  dropped: (args: { item: string; position: number; total: number }) => string
  cancelled: (args: { item: string }) => string
}

export type SortableListProps = {
  /** Current order. The caller renders one `SortableRow` per id, in this order. */
  ids: readonly string[]
  /** The full new order, already applied. Empty when nothing moved. */
  onReorder: (ids: string[]) => void
  /** Resolves an id to the name a screen reader should say. */
  labelFor: (id: string) => string
  announcements: SortableAnnouncements
  children: React.ReactNode
  className?: string | undefined
}

export function SortableList({
  ids,
  onReorder,
  labelFor,
  announcements,
  children,
  className,
}: SortableListProps) {
  const sensors = useSensors(
    // A few pixels of slop: without it every click on a handle registers as a
    // zero-distance drag and the row's own click handler never fires.
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )

  const position = (id: string) => ids.indexOf(id) + 1

  const spoken: Announcements = {
    onDragStart: ({ active }) =>
      announcements.lifted({
        item: labelFor(String(active.id)),
        position: position(String(active.id)),
        total: ids.length,
      }),
    onDragOver: ({ active, over }) =>
      over
        ? announcements.moved({
            item: labelFor(String(active.id)),
            position: position(String(over.id)),
            total: ids.length,
          })
        : undefined,
    onDragEnd: ({ active, over }) =>
      over
        ? announcements.dropped({
            item: labelFor(String(active.id)),
            position: position(String(over.id)),
            total: ids.length,
          })
        : undefined,
    onDragCancel: ({ active }) => announcements.cancelled({ item: labelFor(String(active.id)) }),
  }

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return

    const from = ids.indexOf(String(active.id))
    const to = ids.indexOf(String(over.id))
    if (from === -1 || to === -1) return

    const next = [...ids]
    next.splice(to, 0, ...next.splice(from, 1))
    onReorder(next)
  }

  return (
    <DndContext
      // Never swapped for a shorter list. dnd-kit puts this array straight into
      // a `useEffect` dependency list, so handing it `[]` while a save is in
      // flight changes the dep-array length between renders — React logs
      // "the order and size of this array must remain constant" on every
      // pointer move of the next drag. Suppressing a drag is `SortableRow`'s
      // `disabled`, which is where dnd-kit expects it anyway.
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
      accessibility={{
        announcements: spoken,
        screenReaderInstructions: { draggable: announcements.instructions },
      }}
    >
      <SortableContext items={ids as string[]} strategy={verticalListSortingStrategy}>
        <ul className={cn('space-y-2', className)}>{children}</ul>
      </SortableContext>
    </DndContext>
  )
}

export type SortableRowProps = {
  id: string
  /** Accessible name of the grip button — include the item's own name. */
  handleLabel: string
  children: React.ReactNode
  disabled?: boolean | undefined
  className?: string | undefined
}

/** One row: a grip at the inline start, the caller's content filling the rest. */
export function SortableRow({
  id,
  handleLabel,
  children,
  disabled = false,
  className,
}: SortableRowProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    // The row is the draggable; the grip is what starts the drag. Keeping them
    // separate is what lets the rest of the row stay clickable.
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id, disabled })

  return (
    <li
      ref={setNodeRef}
      style={{
        // x zeroed: the list only moves along one axis, and that axis has no
        // handedness to mirror in Arabic.
        transform: transform ? CSS.Translate.toString({ ...transform, x: 0 }) : undefined,
        transition: transition ?? undefined,
      }}
      className={cn(
        'flex items-start gap-2 rounded-lg border border-line bg-surface',
        // The dragged row lifts off the page rather than merely tinting: colour
        // alone is not a signal (rule 4), and z-index keeps it above its
        // siblings instead of tunnelling under the next card.
        // ⚠️ `ring-brand-fg`: `ring-brand` named no token and rendered nothing,
        // so the lift was shadow-only — and a shadow is invisible in dark mode.
        // Not `ring-focus`: dragging is not focus.
        isDragging && 'relative z-10 shadow-lg ring-2 ring-brand-fg',
        className,
      )}
    >
      <button
        type="button"
        ref={setActivatorNodeRef}
        aria-label={handleLabel}
        disabled={disabled}
        className={cn(
          'mt-1 ms-1 shrink-0 cursor-grab touch-none rounded p-1.5 text-fg-subtle',
          'hover:bg-sunken hover:text-fg focus-visible:ring-2 focus-visible:ring-focus',
          'disabled:cursor-not-allowed disabled:opacity-40',
          isDragging && 'cursor-grabbing',
        )}
        {...attributes}
        {...listeners}
      >
        <GripVertical aria-hidden="true" className="size-4" />
      </button>

      <div className="min-w-0 flex-1">{children}</div>
    </li>
  )
}
